import React, { useContext, useEffect, useRef } from 'react'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SIGNALING_PROVIDERS } from '../../providerIds'
import { createSignalingProviderRegistry } from '../../providers/signaling'
import { SIGNALING_SESSION_EVENTS } from '../../providers/signaling/agora'
import { dispatchWindowSignalingEvent } from '../windowSignalingEvents'

const signalingProviderRegistry = createSignalingProviderRegistry()

export default function ProviderSignalingBridge({
  deviceId,
  dispatchEventKey,
  signalingProvider = SIGNALING_PROVIDERS.agoraRtm,
  onConnectionStatus,
  messageFilter,
  enableLogging = true,
}) {
  const { profileData } = useContext(ProfileContext)
  const onConnectionStatusRef = useRef(onConnectionStatus)
  const messageFilterRef = useRef(messageFilter)

  useEffect(() => {
    onConnectionStatusRef.current = onConnectionStatus
  }, [onConnectionStatus])

  useEffect(() => {
    messageFilterRef.current = messageFilter
  }, [messageFilter])

  useEffect(() => {
    if (!profileData || !deviceId || !dispatchEventKey) {
      return undefined
    }

    const provider = signalingProviderRegistry.get(signalingProvider)

    if (!provider?.createConnection) {
      console.error('[live/signaling] No signaling provider registered for', signalingProvider)
      onConnectionStatusRef.current?.('failed')
      return undefined
    }

    const handle = provider.createConnection({
      deviceId,
      enableLogging,
    })

    let disposed = false

    const removeConnectionStatusListener = handle.on(
      SIGNALING_SESSION_EVENTS.connectionStatusChanged,
      ({ status }) => {
        onConnectionStatusRef.current?.(status)
      },
    )

    const removeMessageListener = handle.on(
      SIGNALING_SESSION_EVENTS.message,
      ({ message, rawMessage }) => {
        const filter = messageFilterRef.current

        if (filter && !filter(message)) {
          return
        }

        dispatchWindowSignalingEvent({
          dispatchEventKey,
          message,
          providerId: signalingProvider,
          deviceId,
          rawMessage,
        })
      },
    )

    const removeErrorListener = handle.on(SIGNALING_SESSION_EVENTS.error, ({ error }) => {
      if (enableLogging) {
        console.error('[live/signaling] Provider signaling error', error)
      }
    })

    handle.connect().catch((error) => {
      if (disposed) {
        return
      }

      console.error('[live/signaling] Failed to connect signaling provider', error)
      onConnectionStatusRef.current?.('failed')
    })

    return () => {
      disposed = true
      removeConnectionStatusListener()
      removeMessageListener()
      removeErrorListener()
      Promise.resolve(handle.destroy()).catch((error) => {
        if (enableLogging) {
          console.warn('[live/signaling] Failed to destroy signaling handle cleanly', error)
        }
      })
    }
  }, [deviceId, dispatchEventKey, enableLogging, profileData, signalingProvider])

  return null
}
